/**
 * Espelho dos contratos do backend, reduzido ao que a tela usa.
 *
 * Escrito à mão em vez de gerado do OpenAPI: o gerador traria os 40 campos de
 * `AnalysisBrief` e as três formas de `Evidence` para uma tela que lê uma dúzia
 * deles, e cada mudança no contrato viraria um diff ilegível. O custo é manter
 * estes tipos em dia — e é por isso que eles ficam num arquivo só.
 */

import type { Role } from './auth'
import { authHeaders, sessionExpired } from './auth'

export type Occupancy = 'minha' | 'disputada' | 'concorrente' | 'branca'
export type Quadrant = 'territorio_branco' | 'campo_de_batalha' | 'fortaleza' | 'descarte'

export interface Evidence {
  unweighted_base: number
  cell_unweighted: number | null
  effective_base: number | null
  ci_low: number | null
  ci_high: number | null
  p_value: number | null
  significant: boolean | null
  suppressed: boolean
  low_base: boolean
}

export interface CompetitorIndex {
  brand_id: string
  index: number
  evidence: Evidence
}

export interface BrandPermission {
  score: number
  rationale: string
  source: string
}

export interface TerritoryPoint {
  territory_id: string
  label: string
  pillar: string
  /** Pasta de topo do catálogo do Profiles; `null` em análise anterior ao campo. */
  folder?: string | null
  affinity_index: number
  competitive_saturation: number
  reach_pct: number
  brand_affinity_index: number
  brand_occupancy: Occupancy
  competitors: CompetitorIndex[]
  brand_permission: BrandPermission | null
  priority_score: number
  adjusted_priority_score: number | null
  quadrant: Quadrant
  evidence: Evidence
  saturation_evidence: Evidence | null
  brand_evidence: Evidence | null
}

export interface SuppressedMetric {
  ref: { kind: string; key: string; field: string }
  label: string
  reason: string
  unweighted_base: number
}

export interface Provenance {
  generated_at: string
  territory_dict_version: string
  brand_registry_version: string
  wave: string | null
  discovery_query_hash: string | null
  query_hashes: string[]
}

export interface Brief {
  audience: { market: string; label: string; filters: { var: string; values: string[] }[] }
  brands: { focus: string; competitors: string[]; category_id: string | null }
  objective: { kpi: string; horizon_days: number }
  config: {
    affinity_threshold: number
    saturation_threshold: number
    occupancy_threshold: number
    min_base: number
    low_base_threshold: number
  }
  label: string | null
}

/** O que o formulário envia: a `config` fica no padrão do servidor. */
export interface BriefInput {
  label: string | null
  audience: {
    market: string
    label: string
    filters: AudienceFilterInput[]
  }
  brands: { focus: string; competitors: string[]; category_id: string }
  objective: { kpi: string }
}

/** Categórica filtra por `values`; array (`selected`) por uma subvariável cada. */
export type AudienceFilterInput =
  | { var: string; op: 'in'; values: string[]; label: string }
  | { var: string; op: 'selected'; subvar: string; label: string }

export interface Catalog {
  market: string
  kpis: string[]
  audience_dimensions: {
    var: string
    label: string
    array?: boolean
    options: { value: string; label: string }[]
  }[]
  categories: {
    id: string
    label: string
    base_unweighted: number | null
    refused_reason: string | null
    brands: { id: string; label: string }[]
  }[]
}

export interface AudienceBase {
  unweighted: number | null
  minimum: number
  recommended: number
}

export interface MatrixResult {
  analysis_id: string
  brief: Brief
  points: TerritoryPoint[]
  suppressed: SuppressedMetric[]
  provenance: Provenance
}

export interface Citation {
  kind: string
  key: string
  field: string
}

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface ChatReply {
  answer: string
  citations: Citation[]
  /** Números da resposta que ninguém da conversa escreveu — o modelo foi instruído a
   * não escrever número; a tela avisa quando escreve. */
  unverified_numbers: string[]
  dropped_citations: string[]
  model_version: string
}

export interface InsightBundle {
  analysis_id: string
  strategic_rationale: { claim: string; citations: Citation[] }[]
  playbook: {
    territory_id: string
    action: string
    rationale: string
    kpi_link: string
    horizon: string | null
    citations: Citation[]
  }[]
  reading_caveats: {
    caveat: string
    impact: string
    territory_id: string | null
    citations: Citation[]
  }[]
  model_version: string
  prompt_version: string
  generated_at: string
  facts_fingerprint: string
}

export interface DiscoverySummary {
  ui_note: string
  audience_variables_excluded: string[]
  n_variables_scanned: number
  n_candidates_tested: number
  n_rejected_by_fdr: number
  n_truncated: number
  vocabulary_version: string
}

export interface AnalysisRow {
  analysis_id: string
  label: string | null
  audience_label: string
  focus: string
  kpi: string
  n_points: number
  n_suppressed: number
  generated_at: string
  wave: string | null
  has_insights: boolean
}

/** 404 é resposta legítima em três leituras desta tela (insights ainda não
 * gerados, análise sem varredura), então ele vira `null` em vez de exceção — o
 * componente decide o que dizer, e um `catch` genérico não distinguiria isso de
 * uma API fora do ar. */
async function get<T>(path: string): Promise<T | null> {
  const response = await fetch(path, { headers: { Accept: 'application/json', ...authHeaders() } })
  if (response.status === 401) return expired(path)
  if (response.status === 404) return null
  if (!response.ok) throw new Error(`${response.status} em ${path}: ${await response.text()}`)
  return (await response.json()) as T
}

async function post(path: string, body?: unknown): Promise<Response> {
  return send('POST', path, body)
}

async function send(method: string, path: string, body?: unknown): Promise<Response> {
  const response = await fetch(path, {
    method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...authHeaders() },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (response.status === 401) return expired(path)
  if (!response.ok) throw new Error(errorDetail(await response.text()) ?? `${response.status} em ${path}`)
  return response
}

/** Sessão caiu no meio do uso (token expirou, acesso revogado no Hub): volta para o
 * Hub, que devolve a pessoa a esta mesma tela. A promessa nunca resolve — a página
 * está sendo trocada, e resolver faria a tela piscar um erro antes de sair. */
function expired(path: string): Promise<never> {
  sessionExpired()
  return new Promise(() => console.info(`sessão expirada em ${path}`))
}

/** O FastAPI devolve `{detail}` — string no erro de domínio, lista no de validação. */
function errorDetail(text: string): string | null {
  try {
    const { detail } = JSON.parse(text) as { detail?: unknown }
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) return detail.map((d: { msg?: string }) => d.msg).join('; ')
  } catch {
    // corpo não-JSON: cai no texto cru
  }
  return text || null
}

export interface Member {
  uid: string
  email: string
  name: string | null
  role: string
  blocked: boolean
}

export interface MemberListing {
  members: Member[]
  roles: Role[]
  /** uid de quem está vendo — a tela não oferece mudar o próprio acesso. */
  me: string
}

export interface SseEvent {
  event: string
  data: unknown
}

/** Separa os eventos completos do buffer e devolve o resto ainda incompleto.
 * `EventSource` não serve aqui: ele só faz GET, e o brief vai no corpo do POST. */
export function parseSse(buffer: string): { events: SseEvent[]; rest: string } {
  const blocks = buffer.split('\n\n')
  const rest = blocks.pop() ?? ''
  const events = blocks.filter(Boolean).map((block) => {
    let event = 'message'
    const data: string[] = []
    for (const line of block.split('\n')) {
      if (line.startsWith('event: ')) event = line.slice(7)
      else if (line.startsWith('data: ')) data.push(line.slice(6))
    }
    return { event, data: JSON.parse(data.join('\n')) as unknown }
  })
  return { events, rest }
}

async function createAnalysis(
  brief: BriefInput,
  onProgress: (stage: string, message: string) => void,
): Promise<MatrixResult> {
  const response = await post('/analyses/stream', brief)
  const reader = response.body!.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''
  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    const parsed = parseSse(buffer + value)
    buffer = parsed.rest
    for (const { event, data } of parsed.events) {
      if (event === 'progress') {
        const { stage, message } = data as { stage: string; message: string }
        onProgress(stage, message)
      } else if (event === 'result') return data as MatrixResult
      else if (event === 'error') throw new Error((data as { detail: string }).detail)
    }
  }
  throw new Error('A conexão terminou antes do resultado. A análise pode ter sido interrompida.')
}

export const api = {
  analyses: () => get<AnalysisRow[]>('/analyses'),
  matrix: (id: string) => get<MatrixResult>(`/analyses/${id}/matrix`),
  insights: (id: string) => get<InsightBundle>(`/analyses/${id}/insights`),
  discovery: (id: string) => get<DiscoverySummary>(`/analyses/${id}/discovery`),
  catalog: () => get<Catalog>('/catalog'),
  audienceBase: async (audience: BriefInput['audience']) =>
    (await (await post('/analyses/audience-base', audience)).json()) as AudienceBase,
  folderWeights: async (id: string) =>
    (await get<{ folder_weights: Record<string, number> }>(`/analyses/${id}/overrides`))
      ?.folder_weights ?? {},
  setFolderWeights: (id: string, weights: Record<string, number>) =>
    send('PUT', `/analyses/${id}/overrides/folders`, weights),
  generateInsights: async (id: string) =>
    (await (await post(`/analyses/${id}/insights`)).json()) as InsightBundle,
  chat: async (id: string, briefing: string, messages: ChatMessage[]) =>
    (await (await post(`/analyses/${id}/chat`, { briefing, messages })).json()) as ChatReply,
  createAnalysis,
  members: {
    list: () => get<MemberListing>('/members'),
    invite: async (email: string, role: Role) =>
      (await (await post('/members/invite', { email, role })).json()) as {
        invite_url: string | null
        email_sent: boolean
      },
    setRole: (uid: string, role: Role) =>
      send('PATCH', `/members/${encodeURIComponent(uid)}/role`, { role }),
    setBlocked: (uid: string, blocked: boolean) =>
      send('POST', `/members/${encodeURIComponent(uid)}/block`, { blocked }),
    remove: (uid: string) => send('DELETE', `/members/${encodeURIComponent(uid)}`),
  },
}
